import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Download, X } from 'lucide-react';

const DISMISS_KEY = 'gd_install_prompt_dismissed';

function safeMatchMedia(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return null;
  }

  try {
    return window.matchMedia(query);
  } catch {
    return null;
  }
}

function isDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Biedt aan om de app te installeren, op een telefoon en op een desktop.
 *
 * Het installeren zelf doet de browser. Chromium vuurt beforeinstallprompt,
 * dat event wordt vastgehouden en opnieuw afgespeeld als je op de knop drukt.
 *
 * Dat event komt alleen kort na het laden en maar een keer, en Chrome wacht
 * bovendien tot je iets met de pagina hebt gedaan. Een luisteraar die pas hier
 * wordt opgehangen mist hem dus vaak. Daarom vangt een scriptje in index.html
 * hem al op en legt hem klaar op window.__ddInstall; hier kijken we of hij er
 * al is en luisteren we daarnaast gewoon verder.
 *
 * Zonder dat event gaan de twee platformen uit elkaar. Op een telefoon valt er
 * altijd iets te zeggen, want iOS Safari installeert via het deelmenu en vuurt
 * niets. Op een desktop hangt het ervan af of de browser het uberhaupt kan:
 * 'onbeforeinstallprompt' in window is waar in Chromium en onwaar in Firefox
 * en Safari, en daar heeft een uitleg over een menu-item dat niet bestaat geen
 * zin.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const mobileMedia = safeMatchMedia('(max-width: 768px)');
    const standaloneMedia = safeMatchMedia('(display-mode: standalone)');

    const updateState = () => {
      const mobile = mobileMedia ? mobileMedia.matches : window.innerWidth <= 768;
      const standaloneByMedia = standaloneMedia ? standaloneMedia.matches : false;
      const standaloneByNavigator = window.navigator.standalone === true;

      setIsMobile(Boolean(mobile));
      setIsStandalone(Boolean(standaloneByMedia || standaloneByNavigator));
    };

    updateState();
    window.addEventListener('resize', updateState);

    return () => {
      window.removeEventListener('resize', updateState);
    };
  }, []);

  useEffect(() => {
    if (isStandalone || isDismissed()) return undefined;

    const pak = () => {
      if (window.__ddInstall) {
        setDeferredPrompt(window.__ddInstall);
        setVisible(true);
      }
    };

    // Het event kan al binnen zijn voordat React stond.
    pak();

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      window.__ddInstall = event;
      setDeferredPrompt(event);
      setVisible(true);
    };

    const onInstalled = () => {
      try {
        localStorage.setItem(DISMISS_KEY, '1');
      } catch {
        // Private mode: dan komt de melding een volgende keer nog eens.
      }
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener('dd-install-ready', pak);
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);

    // Zonder event: op een telefoon altijd iets tonen, op een desktop alleen
    // als de browser installeren uberhaupt ondersteunt.
    const kanInstalleren =
      isMobile || (typeof window !== 'undefined' && 'onbeforeinstallprompt' in window);
    const timer = kanInstalleren ? window.setTimeout(() => setVisible(true), 4000) : null;

    return () => {
      window.removeEventListener('dd-install-ready', pak);
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) window.clearTimeout(timer);
    };
  }, [isMobile, isStandalone]);

  const handleDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Zie boven.
    }
    setVisible(false);
  }, []);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === 'accepted') {
          toast.success('Installatie gestart');
        }
      } catch {
        // De gebruiker sloot de dialoog van de browser zelf.
      }
      window.__ddInstall = null;
      setDeferredPrompt(null);
      handleDismiss();
      return;
    }

    const isiOS = /iPhone|iPad|iPod/i.test(window.navigator.userAgent);
    if (isiOS) {
      toast.info('Open Deel en kies Zet op beginscherm om de app te installeren.');
    } else {
      toast.info('Open het browsermenu rechtsboven en kies App installeren.');
    }
  }, [deferredPrompt, handleDismiss]);

  if (isStandalone || !visible) return null;

  const plek = isMobile
    ? 'bottom-[5.5rem] left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] max-w-sm'
    : 'bottom-6 right-6 w-[21rem]';

  return (
    <div className={`fixed z-[70] ${plek}`}>
      <div className="bg-dd-s1/95 backdrop-blur-xl border border-dd-line rounded-xl px-3 py-2.5 shadow-2xl">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 flex-shrink-0 w-7 h-7 rounded-lg bg-dd-lime/10 border border-dd-lime/25 flex items-center justify-center">
            <Download className="w-3.5 h-3.5 text-dd-lime" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs text-dd-text leading-snug">
              {isMobile
                ? 'Heyy, DriverDash op je beginscherm zetten?'
                : 'Heyy, DriverDash als app op je desktop?'}
            </p>
            <p className="mt-0.5 text-[11px] text-dd-muted leading-snug">
              {deferredPrompt
                ? 'Opent in een eigen venster, zonder browserbalk.'
                : 'Via het browsermenu, in een paar tellen.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Melding sluiten"
            className="flex-shrink-0 -mr-1 -mt-1 p-1 text-dd-dim hover:text-dd-text transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleDismiss}
            className="text-[11px] text-dd-muted hover:text-dd-text transition-colors"
          >
            Later
          </button>
          <button
            type="button"
            onClick={handleInstall}
            className="text-[11px] font-semibold bg-dd-lime text-black px-2.5 py-1 rounded-md hover:bg-dd-lime-dim transition-colors"
          >
            {deferredPrompt ? 'Installeren' : 'Hoe installeren?'}
          </button>
        </div>
      </div>
    </div>
  );
}
